import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, Search, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import { faqData } from '../data/faqData';


const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

// Helper function to format body text with proper structure
const formatBody = (body) => {
  const elements = [];
  let currentList = [];
  let inList = false;

  body.forEach((line, idx) => {
    const isQuestion = line.endsWith('?');
    const isBulletPoint = line.startsWith('•') || line.startsWith('-');
    const isShortLine = line.length < 50 && !line.endsWith('.') && !isQuestion && !line.startsWith('•');
    
    if (isBulletPoint || (isShortLine && idx > 0 && !isQuestion)) {
      if (!inList) {
        inList = true;
        currentList = [];
      }
      currentList.push(line.replace(/^[•-]\s*/, ''));
    } else {
      if (inList && currentList.length > 0) {
        elements.push({ type: 'list', items: [...currentList] });
        currentList = [];
        inList = false;
      }
      
      if (isQuestion) {
        elements.push({ type: 'question', text: line });
      } else {
        elements.push({ type: 'paragraph', text: line });
      }
    }
  });

  if (inList && currentList.length > 0) {
    elements.push({ type: 'list', items: currentList });
  }

  return elements;
};

// FAQ Overview Page Component
export function FAQOverviewPage() {
  const { language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  // Check if a FAQ contains the search query
  const faqContainsQuery = (faq) => {
    if (!searchQuery.trim()) return false;
    const query = searchQuery.toLowerCase();
    const headline = faq.headline[language] || faq.headline.de;
    const body = faq.body[language] || faq.body.de;
    
    if (headline.toLowerCase().includes(query)) return true;
    return body.some(text => text.toLowerCase().includes(query));
  };

  const matchingFaqIds = searchQuery.trim() 
    ? faqData.filter(faq => faqContainsQuery(faq)).map(faq => faq.id)
    : [];

  const hasMatches = matchingFaqIds.length > 0;

  const pageContent = {
    de: {
      subtitle: 'FAQ',
      title: 'Häufig gestellte Fragen',
      description: 'FAQ zu E-Learning, Unterweisungen, Arbeitssicherheit: Fragen für Geschäftsführer, Sicherheitsfachkräfte, HR und IT. SCORM, LMS, Systemvoraussetzungen.',
      seoKeywords: 'FAQ E-Learning, Arbeitssicherheit FAQ, Unterweisungssystem FAQ, SCORM FAQ, LMS FAQ, Sicherheitsfachkräfte, Geschäftsführer Haftung, HR E-Learning, IT Integration E-Learning',
      searchPlaceholder: 'Suchbegriff eingeben...',
      matchesFound: 'Treffer gefunden',
      noMatches: 'Keine Treffer gefunden',
      matchLabel: 'Treffer gefunden',
      ctaTitle: 'Noch Fragen?',
      ctaDescription: 'Unser Team steht Ihnen gerne für weitere Informationen zur Verfügung.',
      ctaButton: 'Kontakt aufnehmen'
    },
    en: {
      subtitle: 'FAQ',
      title: 'Frequently Asked Questions',
      description: 'FAQ about e-learning, training, workplace safety: Questions for executives, safety officers, HR and IT. SCORM, LMS, system requirements.',
      seoKeywords: 'E-Learning FAQ, Workplace Safety FAQ, Training System FAQ, SCORM FAQ, LMS FAQ, Safety Officers, Executive Liability, HR E-Learning, IT Integration E-Learning',
      searchPlaceholder: 'Enter search term...',
      matchesFound: 'matches found',
      noMatches: 'No matches found',
      matchLabel: 'Match found',
      ctaTitle: 'Still have questions?',
      ctaDescription: 'Our team is happy to provide you with more information.',
      ctaButton: 'Contact us'
    }
  };

  const content = pageContent[language] || pageContent.de;

  return (
    <div className="min-h-screen pt-20" data-testid="faq-page">
      <SEO
        title={content.title}
        description={content.description}
        keywords={content.seoKeywords}
        path="/faq"
        lang={language}
      />
      
      <section className="py-16 md:py-20 bg-[#E7F0FA]" data-testid="faq-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-semibold text-[#2E5E99] tracking-wider uppercase mb-4 block">
              {content.subtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0D2440] mb-6">
              {content.title}
            </h1>
            <p className="text-lg md:text-xl text-[#5A7A9B] mb-8">
              {content.description}
            </p>
            
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A7A9B]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={content.searchPlaceholder}
                className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-[#C8D8E8] bg-white text-[#0D2440] placeholder-[#5A7A9B] focus:border-[#2E5E99] focus:outline-none transition-colors"
                data-testid="faq-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A7A9B] hover:text-[#0D2440] transition-colors"
                  data-testid="faq-search-clear"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            {searchQuery.trim() && (
              <p className="mt-4 text-sm text-[#5A7A9B]">
                {hasMatches 
                  ? `${matchingFaqIds.length} ${content.matchesFound}`
                  : content.noMatches}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white" data-testid="faq-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {faqData.map((faq, index) => {
              const isMatch = matchingFaqIds.includes(faq.id);
              const hasSearch = searchQuery.trim().length > 0;
              const headline = faq.headline[language] || faq.headline.de;
              
              return (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.1, 0.5) }}
                >
                  <Link
                    to={`/faq/${faq.id}`}
                    className="block h-full"
                    data-testid={`faq-btn-${faq.id}`}
                  >
                    <div className={`h-full rounded-2xl p-6 transition-all duration-300 group ${
                      (() => {
                        if (isMatch) {
                          return 'bg-[#2E5E99] border-2 border-[#2E5E99] shadow-lg scale-[1.02]';
                        }
                        if (hasSearch && !isMatch) {
                          return 'bg-white border-2 border-[#E7F0FA] opacity-40';
                        }
                        return 'bg-white border-2 border-[#E7F0FA] hover:border-[#2E5E99] hover:shadow-lg';
                      })()
                    }`}>
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                          isMatch 
                            ? 'bg-white/20' 
                            : 'bg-[#2E5E99]/10 group-hover:bg-[#2E5E99]'
                        }`}>
                          <HelpCircle className={`w-6 h-6 transition-colors ${
                            isMatch 
                              ? 'text-white' 
                              : 'text-[#2E5E99] group-hover:text-white'
                          }`} />
                        </div>
                      </div>
                      <h2 className={`text-lg font-bold transition-colors ${
                        isMatch 
                          ? 'text-white' 
                          : 'text-[#0D2440] group-hover:text-[#2E5E99]'
                      }`}>
                        {headline}
                      </h2>
                      {isMatch && (
                        <p className="text-white/80 text-sm mt-2">
                          {content.matchLabel}
                        </p>
                      )}
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#0D2440]" data-testid="faq-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {content.ctaTitle}
            </h2>
            <p className="text-lg text-white/80 mb-8">
              {content.ctaDescription}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#2E5E99] hover:bg-[#3A6DAA] text-white font-medium rounded-xl transition-colors"
              data-testid="faq-cta-button"
            >
              {content.ctaButton}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// FAQ Detail Page Component
export function FAQDetailPage() {
  const { faqId } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const faq = faqData.find(f => f.id === faqId);
  
  const pageContent = {
    de: {
      notFound: 'FAQ nicht gefunden',
      backToFaq: 'Zurück zu FAQ',
      viewAllFaq: 'Alle FAQ ansehen',
      contactUs: 'Kontakt aufnehmen'
    },
    en: {
      notFound: 'FAQ not found',
      backToFaq: 'Back to FAQ',
      viewAllFaq: 'View all FAQ',
      contactUs: 'Contact us'
    }
  };

  const content = pageContent[language] || pageContent.de;
  
  if (!faq) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0D2440] mb-4">
            {content.notFound}
          </h1>
          <Link to="/faq" className="text-[#2E5E99] hover:underline">
            {content.backToFaq}
          </Link>
        </div>
      </div>
    );
  }

  const headline = faq.headline[language] || faq.headline.de;
  const body = faq.body[language] || faq.body.de;
  const formattedBody = formatBody(body);

  return (
    <div className="min-h-screen pt-20" data-testid={`faq-detail-${faq.id}`}>
      <SEO
        title={`${headline} - FAQ`}
        description={body[0]}
        path={`/faq/${faq.id}`}
        lang={language}
      />
      
      <section className="py-12 md:py-16 bg-[#E7F0FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/faq" 
            className="inline-flex items-center text-[#2E5E99] hover:text-[#1E4A7A] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {content.backToFaq}
          </Link>
          
          <motion.div {...fadeUp}>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D2440]">
              {headline}
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="prose prose-lg max-w-none">
            {formattedBody.map((element, idx) => {
              if (element.type === 'question') {
                return (
                  <h3 key={`q-${idx}-${element.text.slice(0,20)}`} className="text-xl font-bold text-[#0D2440] mt-8 mb-4 first:mt-0">
                    {element.text}
                  </h3>
                );
              } else if (element.type === 'list') {
                return (
                  <ul key={`l-${idx}`} className="list-disc list-inside space-y-2 text-[#5A7A9B] mb-4">
                    {element.items.map((item, itemIdx) => (
                      <li key={`li-${idx}-${itemIdx}`}>{item}</li>
                    ))}
                  </ul>
                );
              } else {
                return (
                  <p key={`p-${idx}-${element.text.slice(0,20)}`} className="text-[#5A7A9B] leading-relaxed mb-4">
                    {element.text}
                  </p>
                );
              }
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <Link
              to="/faq"
              className="inline-flex items-center justify-center px-6 py-3 bg-white border border-[#C8D8E8] text-[#0D2440] rounded-xl hover:bg-[#E7F0FA] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {content.viewAllFaq}
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#2E5E99] text-white rounded-xl hover:bg-[#1E4A7A] transition-colors"
            >
              {content.contactUs}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FAQOverviewPage;
